import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import debounce from 'debounce-promise';
import { GroupBase } from 'react-select-5';
import { AsyncProps } from 'react-select-5/async';

import { placeAutocomplete } from 'api/google';
import { PlaceAutocompleteResult } from 'api/google/maps/types';
import { showErrorSnackbar } from 'hooks/useSnackbar';

import { AsyncSelect } from '../../AsyncSelect';

export const PlacesAsyncSelect = function <
  Option extends PlaceAutocompleteResult = PlaceAutocompleteResult,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ ...props }: Partial<AsyncProps<Option, IsMulti, Group>>) {
  const queryClient = useQueryClient();

  const getAddresses = React.useMemo(() => {
    return debounce((address: string): Promise<Option[]> => {
      return queryClient.fetchQuery({
        queryKey: ['geocoding-addresses', address],
        queryFn: async () => {
          try {
            return placeAutocomplete(address);
          } catch (error) {
            let errorMessage = 'Failed to get addresses. Please try again';
            if (error instanceof Error) errorMessage = error.message;
            showErrorSnackbar(errorMessage);
          }
        },
      });
    }, 250);
  }, []);

  return (
    <AsyncSelect
      loadOptions={getAddresses}
      getOptionLabel={(res) => res.description}
      placeholder="Enter address..."
      {...props}
    />
  );
};
